/**
 * Socket.io connection hook with auto-reconnect and visibility-change recovery.
 * Replaces raw WebSocket hook — same interface for consuming components.
 */

import { useState, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

interface UseTicketWebSocketProps {
  ticketId: string;        // Ticket ID or 'stats' for dashboard
  authToken: string;       // JWT token for authentication
  enabled?: boolean;       // Allow disabling (default true)
  onOpen?: () => void;     // Called every time connection opens (including reconnects)
}

interface WebSocketMessage {
  // v4.1 added 'message_deleted' — fires when one or more messages are
  // soft-deleted server-side; the consumer (TicketDetail) refreshes the
  // conversation tail so the UI renders the "Message deleted" placeholder
  // in place of the original bubbles.
  // edit-message phase added 'message_edited' — fires when a technician
  // edits a message via PATCH /tickets/.../messages/:id. Consumer patches
  // the row's text in conversationMessages directly.
  type:
    | 'new_ticket'
    | 'new_message'
    | 'ticket_updated'
    | 'stats_updated'
    | 'message_deleted'
    | 'message_edited'
    | 'broadcast_progress';
  ticketId: string;
  payload: unknown;
}

interface UseTicketWebSocketReturn {
  isConnected: boolean;
  lastMessage: WebSocketMessage | null;
  error: string | null;
}

export function useTicketWebSocket({
  ticketId,
  authToken,
  enabled = true,
  onOpen,
}: UseTicketWebSocketProps): UseTicketWebSocketReturn {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const socketRef = useRef<Socket | null>(null);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    if (!enabled || !authToken || !ticketId) return;

    const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;

    const socket = io(apiUrl, {
      auth: { token: authToken },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      setError(null);
      // Join the appropriate room after every connect (including reconnects)
      socket.emit('join', { ticketId });
      // Fire onOpen callback — components use this to refetch data after reconnection gaps
      onOpenRef.current?.();
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('Socket.io connection error:', err.message);
      setError(err.message);
    });

    // Listen for all event types and wrap in WebSocketMessage shape
    const eventTypes = ['new_ticket', 'new_message', 'ticket_updated', 'stats_updated', 'message_deleted', 'message_edited', 'broadcast_progress'] as const;
    for (const eventType of eventTypes) {
      socket.on(eventType, (data: WebSocketMessage) => {
        setLastMessage(data);
      });
    }

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled, authToken, ticketId]);

  // Force-reconnect when the app comes back to foreground.
  // iOS (DgSuperApp WKWebView) kills WebSocket connections when the screen locks
  // or the app is backgrounded. Socket.io's built-in reconnect won't fire until
  // the next heartbeat timeout — this reconnects immediately.
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const socket = socketRef.current;
        if (socket && socket.disconnected) {
          console.log(`Socket.io visibility reconnect: ${ticketId}`);
          socket.connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled, ticketId]);

  return { isConnected, lastMessage, error };
}
